import { httpClient } from "@bw/shared/api";

export type ExperienceClaim = {
  claim_id: string; coach_profile_id: string; source_org_id: string;
  coach_display_name: string; status: "pending_club" | "approved" | "rejected" | "platform_review";
  claimant_display_name: string; identity_proof_kind: "firebase_verified_email" | "platform_recovery";
  identity_proof_checked_at: string;
  visibility: "private" | "shared_clubs"; revision: number;
  decision_reason: string | null; created_at: string; resolved_at: string | null;
};
export type CoachingExperienceAccess = {
  can_access: boolean;
  active_coach: boolean;
  has_claim_history: boolean;
};
export type PortableExperience = {
  coaching_minutes: number; sessions_taught: number; evidence_revision: number;
  clubs: { source_org_id: string; coaching_minutes: number; sessions_taught: number; unique_students: number | null }[];
  achievements: { achievement_id: string; provenance: string; title: string; category: string; earned_on: string; visibility: "private" | "shared_clubs"; revision: number }[];
};
export type ExperienceShare = {
  share_id: string; claim_id: string; coach_display_name: string;
  destination_org_id: string; destination_coach_profile_id: string | null;
  status: "pending_destination" | "active" | "rejected" | "revoked";
  scopes: ExperienceShareScope[]; revision: number;
};
export type ExperienceShareScope =
  | "coaching_minutes"
  | "sessions_taught"
  | "per_club_unique_students"
  | "achievements";

export const experienceApi = {
  access: () =>
    httpClient.get<CoachingExperienceAccess>("/coach-experience/access/mine"),
  claims: () => httpClient.get<ExperienceClaim[]>("/coach-experience/claims/mine"),
  summary: () => httpClient.get<PortableExperience>("/coach-experience/summary/mine"),
  shares: () => httpClient.get<ExperienceShare[]>("/coach-experience/shares/mine"),
  claim: (coachProfileId: string) => httpClient.post<ExperienceClaim>("/coach-experience/claims", {
    coach_profile_id: coachProfileId, idempotency_key: crypto.randomUUID(),
  }),
  visibility: (claim: ExperienceClaim, visibility: ExperienceClaim["visibility"]) =>
    httpClient.put<ExperienceClaim>(`/coach-experience/claims/${encodeURIComponent(claim.claim_id)}/visibility`, {
      visibility, expected_revision: claim.revision, idempotency_key: crypto.randomUUID(),
    }),
  dispute: (claim: ExperienceClaim, reason: string) => httpClient.post<ExperienceClaim>(
    `/coach-experience/claims/${encodeURIComponent(claim.claim_id)}/dispute`, {
      reason, expected_revision: claim.revision, idempotency_key: crypto.randomUUID(),
    }),
  share: (
    claim: ExperienceClaim,
    destination: ExperienceClaim,
    scopes: ExperienceShareScope[],
  ) => httpClient.post<ExperienceShare>(
    `/coach-experience/claims/${encodeURIComponent(claim.claim_id)}/shares`, {
      destination_org_id: destination.source_org_id,
      destination_coach_profile_id: destination.coach_profile_id,
      scopes,
      idempotency_key: crypto.randomUUID(),
    }),
  revokeShare: (share: ExperienceShare) => httpClient.post<ExperienceShare>(
    `/coach-experience/shares/${encodeURIComponent(share.share_id)}/revoke`, {
      reason: "Revoked by coach",
      expected_revision: share.revision,
      idempotency_key: crypto.randomUUID(),
    }),
  achievementVisibility: (achievement: PortableExperience["achievements"][number], visibility: "private" | "shared_clubs") =>
    httpClient.put(`/coach-experience/achievements/${encodeURIComponent(achievement.achievement_id)}/visibility`, {
      visibility, expected_revision: achievement.revision, idempotency_key: crypto.randomUUID(),
    }),
};
